import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, KeyRound, Mail, Loader2, AlertCircle } from "lucide-react";

export default function AccountPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    current_password: "",
    new_email: user?.email || "",
    new_password: "",
    new_password_confirm: "",
  });
  const [saving, setSaving] = useState(false);
  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password) {
      toast.error("Veuillez saisir votre mot de passe actuel");
      return;
    }
    const emailChanged = form.new_email && form.new_email.toLowerCase() !== (user?.email || "").toLowerCase();
    const passwordChanged = !!form.new_password;
    if (!emailChanged && !passwordChanged) {
      toast.error("Aucune modification : changez l'email ou le mot de passe");
      return;
    }
    if (passwordChanged) {
      if (form.new_password.length < 8) {
        toast.error("Le nouveau mot de passe doit faire au moins 8 caractères");
        return;
      }
      if (form.new_password !== form.new_password_confirm) {
        toast.error("La confirmation du mot de passe ne correspond pas");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { current_password: form.current_password };
      if (emailChanged) payload.new_email = form.new_email.trim();
      if (passwordChanged) payload.new_password = form.new_password;
      await api.post("/auth/me/credentials", payload);
      toast.success("Identifiants mis à jour — veuillez vous reconnecter");
      // Force logout so the new credentials are picked up cleanly
      setTimeout(async () => {
        await logout();
        navigate("/login");
      }, 1200);
    } catch (err) {
      toast.error(formatApiError(err, "Mise à jour impossible"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 max-w-2xl" data-testid="account-panel">
      <div className="card p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-md grid place-items-center shrink-0" style={{ background: "var(--c-primary)", color: "var(--c-accent)" }}>
            <ShieldCheck size={18}/>
          </span>
          <div className="min-w-0">
            <div className="overline">Mon compte</div>
            <h2 className="font-display text-2xl font-bold mt-1" style={{ color: "var(--c-primary)" }}>
              Identifiants administrateur
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              Email actuel : <span className="font-medium" data-testid="account-current-email">{user?.email || "—"}</span>
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-md border px-4 py-3 flex items-start gap-3 text-xs sm:text-sm leading-relaxed" style={{ background: "rgba(212,160,64,0.10)", borderColor: "rgba(212,160,64,0.40)", color: "var(--c-primary)" }}>
          <AlertCircle size={16} className="mt-0.5 shrink-0"/>
          <span>
            Après modification, vous serez automatiquement déconnecté et devrez vous reconnecter avec vos nouveaux identifiants.
            Conservez-les dans un endroit sûr.
          </span>
        </div>

        <form onSubmit={submit} className="mt-7 space-y-5" data-testid="account-form">
          <div>
            <label className="field-label" htmlFor="acc-current-password">
              <KeyRound size={11} className="inline mr-1 -mt-0.5"/> Mot de passe actuel *
            </label>
            <input
              id="acc-current-password"
              data-testid="account-current-password"
              type="password"
              required
              autoComplete="current-password"
              className="field-input"
              value={form.current_password}
              onChange={update("current_password")}
              placeholder="Votre mot de passe actuel"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-5 pt-4 border-t border-slate-200">
            <div className="sm:col-span-2">
              <p className="overline" style={{ color: "var(--c-text-soft)" }}>Nouveau email (optionnel)</p>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label" htmlFor="acc-new-email">
                <Mail size={11} className="inline mr-1 -mt-0.5"/> Nouvel email
              </label>
              <input
                id="acc-new-email"
                data-testid="account-new-email"
                type="email"
                autoComplete="email"
                className="field-input"
                value={form.new_email}
                onChange={update("new_email")}
                placeholder="ex. collegelyceelafayette@gmail.com"
              />
              <p className="text-xs text-slate-500 mt-1">Laissez identique pour ne pas changer l'email.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 pt-4 border-t border-slate-200">
            <div className="sm:col-span-2">
              <p className="overline" style={{ color: "var(--c-text-soft)" }}>Nouveau mot de passe (optionnel)</p>
            </div>
            <div>
              <label className="field-label" htmlFor="acc-new-password">
                <KeyRound size={11} className="inline mr-1 -mt-0.5"/> Nouveau mot de passe
              </label>
              <input
                id="acc-new-password"
                data-testid="account-new-password"
                type="password"
                autoComplete="new-password"
                className="field-input"
                value={form.new_password}
                onChange={update("new_password")}
                placeholder="Au moins 8 caractères"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="acc-new-password-confirm">
                <KeyRound size={11} className="inline mr-1 -mt-0.5"/> Confirmer
              </label>
              <input
                id="acc-new-password-confirm"
                data-testid="account-new-password-confirm"
                type="password"
                autoComplete="new-password"
                className="field-input"
                value={form.new_password_confirm}
                onChange={update("new_password_confirm")}
                placeholder="Retapez le nouveau mot de passe"
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="account-submit"
            disabled={saving}
            className="btn-cta justify-center"
          >
            {saving ? (<><Loader2 size={16} className="animate-spin"/> Enregistrement…</>) : (<>Enregistrer les nouveaux identifiants</>)}
          </button>
        </form>
      </div>
    </div>
  );
}
